<template>
  <div class="share-page">
    <StepProgress />
    
    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Share
            </v-card-title>
            
            <v-card-text class="pa-6">
              <!-- Success Message -->
              <div class="text-center mb-8">
                <v-icon size="80" color="success" class="mb-4">
                  mdi-check-circle
                </v-icon>
                <h2 class="text-h4 mb-4">Memory Created Successfully!</h2>
                <p class="text-h6 text--secondary mb-2">
                  Your beautiful memory for {{ store.personName }} is now ready to share.
                </p>
              </div>

              <!-- Email Invitations -->
              <v-card class="invite-card mb-6" elevation="0">
                <v-card-text class="text-center pa-6">
                  <v-icon size="56" color="primary" class="mb-3">mdi-email-outline</v-icon>
                  <p v-if="validContacts.emails.length" class="text-body-1 text--secondary mb-4">
                    We’ve saved the email addresses you added. Send them a personal invitation so they can contribute their memories. This can be done later from the Dashboard.
                  </p>
                  <p v-else class="text-body-1 text--secondary mb-0">
                    You haven’t added any contacts yet. You can add contacts later from the Dashboard.
                  </p>
                  <v-btn
                    v-if="validContacts.emails.length"
                    color="primary"
                    size="large"
                    class="px-6"
                    @click="openEmailShare"
                  >
                    Send Email Invitations
                  </v-btn>
                  <p v-if="validContacts.emails.length" class="text-caption text--secondary mt-3">
                    {{ validContacts.emails.length }} email recipient{{ validContacts.emails.length === 1 ? '' : 's' }} selected.
                  </p>
                </v-card-text>
              </v-card>

              <!-- Memorial Link & Social Sharing -->
              <v-card class="memorial-link-card mb-6" elevation="0">
                <v-card-title class="link-header">
                  <v-icon left color="primary">mdi-link</v-icon>
                  Memory Link
                </v-card-title>
                
                <v-card-text>
                  <div class="link-display mb-4">
                    <v-text-field
                      :model-value="memorialLink"
                      label="Memory URL"
                      variant="outlined"
                      readonly
                      density="comfortable"
                      class="memorial-url"
                      persistent-placeholder
                    >
                      <template v-slot:append>
                        <v-btn
                          icon
                          size="small"
                          @click="copyLink"
                          :color="linkCopied ? 'success' : 'primary'"
                        >
                          <v-icon>{{ linkCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>

                  <p class="text-body-2 text--secondary mb-4 link-copy-note">
                    Copy this link and paste it into your social media posts or group chats so everyone can visit the memory.
                  </p>

                  <div class="social-share">
                    <p class="text-body-2 mb-3">Quick share:</p>
                    <div class="social-buttons">
                      <v-btn
                        icon
                        size="large"
                        color="#1877f2"
                        @click="shareOnFacebook"
                        class="mr-2"
                      >
                        <v-icon>mdi-facebook</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="large"
                        color="#1da1f2"
                        @click="shareOnTwitter"
                        class="mr-2"
                      >
                        <v-icon>mdi-twitter</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="large"
                        color="#25d366"
                        @click="shareOnWhatsApp"
                      >
                        <v-icon>mdi-whatsapp</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Next Steps -->
              <v-card class="next-steps-card" elevation="0">
                <v-card-title class="next-steps-header">
                  <v-icon left color="primary">mdi-lightbulb-outline</v-icon>
                  What Happens Next?
                </v-card-title>
                
                <v-card-text>
                  <v-list density="compact" class="next-steps-list">
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-numeric-1-circle</v-icon>
                      </template>
                      <v-list-item-title>Family and friends can view the memory using the link above</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-numeric-2-circle</v-icon>
                      </template>
                      <v-list-item-title>They can add their own photos and stories to honor {{ store.personName }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-numeric-3-circle</v-icon>
                      </template>
                      <v-list-item-title>You'll receive notifications when new contributions are added</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-numeric-4-circle</v-icon>
                      </template>
                      <v-list-item-title>You can always update or modify the memory from your account</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="previousStep"
                class="mr-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              
              <v-spacer></v-spacer>
              
              <v-btn
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                @click="goToMemorial"
                class="px-8"
              >
                View Memory
                <v-icon right class="ml-2">mdi-eye</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Email Share Dialog -->
    <v-dialog v-model="showEmailDialog" max-width="600">
      <v-card>
        <v-card-title>Send Email Invitations</v-card-title>
        <v-card-text>
          <p class="mb-4">Send invitations to the contacts you added earlier:</p>

          <div class="contact-preview">
            <h4 class="text-h6 mb-2">Email Recipients:</h4>
            <v-chip
              v-for="email in validContacts.emails"
              :key="email"
              class="mr-2 mb-2"
              color="primary"
              variant="outlined"
            >
              {{ email }}
            </v-chip>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showEmailDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="sendEmailInvitations" :loading="sendingEmails">
            Send Invitations
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Result Dialog -->
    <v-dialog v-model="showResultDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon :color="resultDialogSuccess ? 'success' : 'error'" class="mr-2">
            {{ resultDialogSuccess ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          {{ resultDialogSuccess ? 'Success' : 'Error' }}
        </v-card-title>
        <v-card-text>
          <p>{{ resultDialogMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showResultDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import StepProgress from '../components/StepProgress.vue'
import apiService from '../services/api'
import { APP_BASE_URL } from '@/config/branding'

const router = useRouter()
const store = useMemorialStore()

// State
const linkCopied = ref(false)
const showEmailDialog = ref(false)
const sendingEmails = ref(false)
const showResultDialog = ref(false)
const resultDialogSuccess = ref(false)
const resultDialogMessage = ref('')

const MEMORIAL_BASE_URL = process.env.NODE_ENV === 'production'
  ? APP_BASE_URL
  : 'http://localhost'

// Computed
const memorialSlug = computed(() => store.shareSlug || store.ensureShareSlug())

const memorialLink = computed(() => {
  return `${MEMORIAL_BASE_URL}/for/${memorialSlug.value}`
})

const validContacts = computed(() => {
  return {
    emails: store.contacts.filter(contact => 
      contact && contact.trim() && /.+@.+\..+/.test(contact.trim())
    ),
    phones: store.contacts.filter(contact => 
      contact && contact.trim() && /^[+]?[\s\-()]*([0-9][\s\-()]*){7,}/.test(contact.trim())
    )
  }
})

// Methods
const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(memorialLink.value)
    linkCopied.value = true
    setTimeout(() => {
      linkCopied.value = false
    }, 2000)
  } catch (error) {
    // Fallback for browsers that don't support clipboard API
    const textArea = document.createElement('textarea')
    textArea.value = memorialLink.value
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    linkCopied.value = true
    setTimeout(() => {
      linkCopied.value = false
    }, 2000)
  }
}

const openEmailShare = () => {
  showEmailDialog.value = true
}

const sendEmailInvitations = async () => {
  sendingEmails.value = true

  try {
    if (!store.memorialUuid) {
      resultDialogSuccess.value = false
      resultDialogMessage.value = 'Memorial UUID is missing. Please try again.'
      showResultDialog.value = true
      return
    }

    if (!store.apiToken) {
      resultDialogSuccess.value = false
      resultDialogMessage.value = 'You must be logged in to send invitations.'
      showResultDialog.value = true
      return
    }

    const response = await apiService.sendAllMemorialInvites(
      store.memorialUuid,
      store.apiToken
    )

    if (response.success) {
      showEmailDialog.value = false
      resultDialogSuccess.value = true
      resultDialogMessage.value = response.msg || `Email invitations sent to ${response.sentCount} recipient${response.sentCount === 1 ? '' : 's'}!`
      showResultDialog.value = true
    } else {
      resultDialogSuccess.value = false
      resultDialogMessage.value = response.msg || 'Failed to send invitations. Please try again.'
      showResultDialog.value = true
    }
  } catch (error) {
    console.error('Error sending invitations:', error)
    resultDialogSuccess.value = false
    resultDialogMessage.value = 'An error occurred while sending invitations. Please try again.'
    showResultDialog.value = true
  } finally {
    sendingEmails.value = false
  }
}

const shareOnFacebook = () => {
  const url = encodeURIComponent(memorialLink.value)
  const text = encodeURIComponent(`Please join me in celebrating the life of ${store.personName}. View their memory and share your own memories.`)
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`, '_blank')
}

const shareOnTwitter = () => {
  const url = encodeURIComponent(memorialLink.value)
  const text = encodeURIComponent(`Celebrating the life of ${store.personName}. Share your memories: ${url}`)
  window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank')
}

const shareOnWhatsApp = () => {
  const text = encodeURIComponent(`Please join me in celebrating the life of ${store.personName}. View their memory and share your own memories: ${memorialLink.value}`)
  window.open(`https://wa.me/?text=${text}`, '_blank')
}

const goToMemorial = () => {
  router.push('/memory/feed')
}

// Navigation
const previousStep = () => {
  store.goToStep(7) // Contacts step
  router.push('/contacts')
}

// Lifecycle
onMounted(() => {
  console.log('SharePage mounted - store.contacts:', store.contacts)
  store.goToStep(8) // Set current step to Share
  const slug = store.ensureShareSlug()

  if (store.shareSlugPendingPersist && store.memorialUuid && store.apiToken) {
    apiService.updateShareSlug(store.memorialUuid, slug, store.apiToken)
      .then(response => {
        if (response.success && response.memorial?.shareSlug) {
          store.setShareSlug(response.memorial.shareSlug)
        } else if (response.success) {
          store.markShareSlugPersisted()
        }
      })
      .catch(error => {
        console.error('Failed to persist share slug:', error)
      })
  }
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;
.share-page {
  @include page-container;
}

.main-card {
  @include main-card;
  
  .v-card-title {
    @include section-header;
  }
}

.memorial-link-card,
.next-steps-card,
.invite-card {
  @include card-base;
}

.link-header,
.next-steps-header {
  background: var(--v-theme-background);
  border-bottom: 1px solid var(--v-theme-border);
  font-size: 1.1rem !important;
  font-weight: 600;
}

.invite-card {
  background: var(--v-theme-background);
}

.memorial-url {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
}

.social-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
}

.social-share {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.link-copy-note {
  max-width: 520px;
}

.next-steps-list {
  background: transparent;
}

.contact-preview {
  background: var(--v-theme-background);
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid var(--v-theme-border);
}

@include mobile {
  .pa-6 {
    padding: 1rem !important;
  }
  
  .social-buttons {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
    
    .mr-2 {
      margin-right: 0 !important;
    }
  }
}
</style>
